import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { Loader2, Pause, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useBrand } from '@/hooks/useBrand'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface PausePushDialogProps {
  /** The property's backend-served canonical path. The PATCH target is this + '/go-live'. */
  propertyBasePath: string
  propertyName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Password-confirmed pause.
 *
 * Pausing is the silent-damage direction of the go-live toggle: rates quietly stop
 * reaching the PMS and the property drifts off-strategy with no outward signal. So the
 * actor re-proves they are the account holder before it takes effect (step-up auth).
 *
 * The password travels in the PATCH body of the SAME request that performs the pause —
 * there is no separate "verify" call, so there is no window between proving identity and
 * the action running. The server (ToggleGoLiveMutation → VerifyPasswordConfirmationMutation)
 * is the only authority; this dialog just collects and displays.
 *
 * Errors are read from the request's own onError payload rather than the global `errors`
 * page prop, so an unrelated form's validation errors can never surface here.
 */
export default function PausePushDialog({ propertyBasePath, propertyName, open, onOpenChange }: PausePushDialogProps) {
  const brand = useBrand()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function reset() {
    setPassword('')
    setError(null)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return

    router.patch(
      `${propertyBasePath}/go-live`,
      { live: false, password },
      {
        // The badge lives in the header, so a pause can be triggered from any property
        // page. Preserving state + scroll keeps this dialog mounted (and the operator in
        // place) when the server bounces back a password error.
        preserveState: true,
        preserveScroll: true,
        onStart: () => {
          setSubmitting(true)
          setError(null)
        },
        onError: (errors) => {
          setError((errors.password as string) ?? 'Could not pause rate pushing. Please try again.')
        },
        onSuccess: () => {
          reset()
          onOpenChange(false)
        },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Pause rate pushing?</DialogTitle>
          <DialogDescription>
            {brand} will stop sending rates to your PMS for{' '}
            <span className="font-medium text-foreground">{propertyName}</span>. Computations keep
            running, but nothing reaches your PMS until you go live again.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            <div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Because paused rates stop reaching your PMS silently, confirm your password to
                continue.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pause-password">Your password</Label>
              <Input
                id="pause-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'pause-password-error' : undefined}
                className={error ? 'border-rose-300 focus-visible:ring-rose-400' : undefined}
              />
              {error && (
                <p id="pause-password-error" role="alert" className="text-xs font-medium text-rose-600">
                  {error}
                </p>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || password === ''}
              className="rounded-lg bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-400 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Pausing…
                </>
              ) : (
                <>
                  <Pause className="mr-1.5 h-3.5 w-3.5" />
                  Pause rate pushing
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
